import { Input, Stack } from "@chakra-ui/react";
import * as React from "react";

import { validateFacebookCapiToken } from "../api/integration";
import { createSuppressionAudience } from "../api/suppression-audience";
import {
  useAppDispatch,
  useAppSelector,
  useLoading,
  useToaster,
} from "../hooks";
import { loadSuppressionAudienceFields } from "../store/workspaceSlice";
import { IntegrationStatus } from "../types/integration";
import { Button } from "./button";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "./dialog";
import {
  FacebookAdAccountField,
  FacebookNotConnectedMessage,
} from "./facebook-ad-account-field";
import { FacebookCapiCredentialsFields } from "./facebook-capi-credentials-fields";
import { Field } from "./field";
import {
  RuleEditor,
  buildRules,
  defaultRoot,
  isRootValid,
} from "./rule-editor";
import { toaster } from "./toaster";

type ModalStep = "account" | "details" | "rules";

interface CreateSuppressionAudienceDialogProps {
  isOpen: boolean;
  onOpenChange: (details: { open: boolean }) => void;
  onSuccess: () => void;
  workspaceSlug: string;
}

export function CreateSuppressionAudienceDialog({
  isOpen,
  onOpenChange,
  onSuccess,
  workspaceSlug,
}: CreateSuppressionAudienceDialogProps) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [currentStep, setCurrentStep] = React.useState<ModalStep>("account");

  const [selectedAdAccount, setSelectedAdAccount] = React.useState<string>("");
  const [selectedDataset, setSelectedDataset] = React.useState<string>("");
  const [accessToken, setAccessToken] = React.useState<string>("");
  const [name, setName] = React.useState<string>("");
  const [ruleRoot, setRuleRoot] = React.useState(defaultRoot());

  const [isLoading, withLoading] = useLoading();
  const dispatch = useAppDispatch();
  const withToaster = useToaster();
  const fields = useAppSelector(
    (state) => state.workspace.suppressionAudienceFields,
  );
  const isLoadingFields = useAppSelector(
    (state) => state.workspace.isLoadingSuppressionAudienceFields,
  );
  const integrations = useAppSelector((state) => state.workspace.integrations);

  const isConnected = React.useMemo(() => {
    return integrations.some(
      (i) => i.id === "facebook" && i.status === IntegrationStatus.connected,
    );
  }, [integrations]);

  const resetState = React.useCallback(() => {
    setCurrentStep("account");
    setSelectedAdAccount("");
    setSelectedDataset("");
    setAccessToken("");
    setName("");
    setRuleRoot(defaultRoot());
  }, []);

  const handleOpenChange = React.useCallback(
    (details: { open: boolean }) => {
      if (!details.open) {
        resetState();
      }
      onOpenChange(details);
    },
    [onOpenChange, resetState],
  );

  React.useEffect(() => {
    if (!isOpen) return;
    if (isLoadingFields || fields.length > 0) return;
    dispatch(loadSuppressionAudienceFields());
  }, [dispatch, fields.length, isLoadingFields, isOpen]);

  const handleNext = async () => {
    if (currentStep === "account") {
      setCurrentStep("details");
    } else if (currentStep === "details") {
      await withLoading(async () => {
        await withToaster(async () => {
          await validateFacebookCapiToken(
            workspaceSlug,
            selectedDataset,
            accessToken.trim(),
          );
          setCurrentStep("rules");
        });
      });
    }
  };

  const handleBack = () => {
    if (currentStep === "rules") {
      setCurrentStep("details");
    } else if (currentStep === "details") {
      setCurrentStep("account");
    }
  };

  const canProceed = (): boolean => {
    if (!isConnected) return false;
    if (currentStep === "account") {
      return selectedAdAccount !== "";
    }
    if (currentStep === "details") {
      return (
        name.trim() !== "" &&
        selectedDataset !== "" &&
        accessToken.trim() !== ""
      );
    }
    return false;
  };

  const handleCreate = async () => {
    await withLoading(async () => {
      await withToaster(async () => {
        await createSuppressionAudience(workspaceSlug, {
          "access-token": accessToken.trim(),
          "ad-account-id": selectedAdAccount,
          "dataset-id": selectedDataset,
          name: name.trim(),
          rules: buildRules(ruleRoot, fields),
        });

        toaster.create({
          type: "success",
          title: "Exclusion audience created",
          description: `"${name.trim()}" has been created successfully.`,
        });

        handleOpenChange({ open: false });
        onSuccess();
      });
    });
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case "account":
        return "Select Ad Account";
      case "details":
        return "Audience Details";
      case "rules":
        return "Configure Rules";
    }
  };

  const renderAccountStep = () => {
    if (!isConnected) {
      return (
        <FacebookNotConnectedMessage purpose="create exclusion audiences" />
      );
    }
    return (
      <FacebookAdAccountField
        workspaceSlug={workspaceSlug}
        value={selectedAdAccount}
        onChange={setSelectedAdAccount}
        portalRef={ref as React.RefObject<HTMLElement>}
      />
    );
  };

  const renderDetailsStep = () => (
    <Stack gap="4">
      <Field label="Name" required>
        <Input
          size="sm"
          placeholder="Enter audience name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>
      <FacebookCapiCredentialsFields
        workspaceSlug={workspaceSlug}
        adAccountId={selectedAdAccount}
        dataset={selectedDataset}
        onDatasetChange={setSelectedDataset}
        accessToken={accessToken}
        onAccessTokenChange={setAccessToken}
        portalRef={ref as React.RefObject<HTMLElement>}
        datasetHelperText="The dataset where customer exclusion events are sent."
      />
    </Stack>
  );

  return (
    <DialogRoot open={isOpen} onOpenChange={handleOpenChange} size="lg">
      <DialogContent ref={ref}>
        <DialogCloseTrigger />
        <DialogHeader>
          <DialogTitle>{getStepTitle()}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Stack gap="4">
            {currentStep === "account" && renderAccountStep()}
            {currentStep === "details" && renderDetailsStep()}
            {currentStep === "rules" && (
              <RuleEditor
                value={ruleRoot}
                fields={fields}
                portalRef={ref as React.RefObject<HTMLElement>}
                onChange={setRuleRoot}
              />
            )}
          </Stack>
        </DialogBody>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange({ open: false })}
          >
            Cancel
          </Button>
          {currentStep !== "account" && (
            <Button variant="outline" onClick={handleBack}>
              Back
            </Button>
          )}
          {currentStep !== "rules" ? (
            <Button
              onClick={handleNext}
              disabled={!canProceed()}
              loading={isLoading}
            >
              Next
            </Button>
          ) : (
            <Button
              onClick={handleCreate}
              loading={isLoading}
              disabled={!isRootValid(ruleRoot)}
            >
              Create
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
